import { ActionIcon, Flex, Menu, Tooltip } from "@mantine/core";
import { DotsThreeIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classes from "./list.module.css";
import { RowActionButton, RowActionIntent } from "./RowActionButton";

export type RowActionItem = {
  key: string;
  intent: RowActionIntent;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  hidden?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  loading?: boolean;
};

const ACTION_GAP = 2;

type Props = {
  items: RowActionItem[];
  /** When true, never collapse into a ⋮ menu (open invoice list). Default false keeps overflow menu. */
  inlineOnly?: boolean;
};

export function RowActions({ items, inlineOnly = false }: Props) {
  const { t } = useTranslation("common");
  const visible = useMemo(() => items.filter((item) => !item.hidden), [items]);

  if (visible.length === 0) {
    return null;
  }

  const visibleMax = inlineOnly ? visible.length : 3;
  const primary = visible.length > visibleMax ? visible.slice(0, visibleMax) : visible;
  const overflow = visible.length > visibleMax ? visible.slice(visibleMax) : [];

  return (
    <Flex className={classes.rowActions} justify="flex-start" align="center" gap={ACTION_GAP}>
      {primary.map((item) => (
        <RowActionButton
          key={item.key}
          intent={item.intent}
          icon={item.icon}
          label={item.label}
          onClick={item.onClick}
          disabled={item.disabled}
          disabledReason={item.disabledReason}
          loading={item.loading}
        />
      ))}
      {overflow.length > 0 && (
        <Menu transitionProps={{ duration: 150, transition: "pop" }} position="bottom-start" shadow="md" radius="md">
          <Menu.Target>
            <Tooltip label={t("list.moreActions")} withArrow>
              <ActionIcon variant="subtle" color="gray" size="lg" radius="md" aria-label={t("list.moreActions")}>
                <DotsThreeIcon size={20} weight="bold" />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            {overflow.map((item) => (
              <Menu.Item
                key={item.key}
                leftSection={item.icon}
                disabled={item.disabled || item.loading}
                onClick={item.onClick}
              >
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Flex>
  );
}
